// src/components/atomic-crm/providers/supabase/data-provider/handlers/create.ts
import type { DataProvider } from "ra-core";
import { supabase } from "../../supabase";
import { fetchEnrichedRecord } from "../enrichment";
import {
  stripTaskSummaryFields,
  stripDealSummaryFields,
  stripInvoiceSummaryFields,
  stripCrmActivityFields,
} from "../transforms";

// ---------------------------------------------------------------------------
// org_id stamping (multi-tenant)
// ---------------------------------------------------------------------------

const TENANT_TABLES = new Set([
  "contacts",
  "companies",
  "deals",
  "crm_activities",
  "invoices",
  "invoice_items",
  "invoice_notes",
  "invoice_templates",
  "invoice_template_items",
  "tags",
  "tasks",  // if you still have it
]);

let cachedOrgId: string | null | undefined = undefined;

const getCurrentOrgId = async (): Promise<string | null> => {
  if (cachedOrgId !== undefined) return cachedOrgId;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    cachedOrgId = null;
    return null;
  }

  const { data: sale } = await supabase
    .from("sales")
    .select("org_id")
    .eq("user_id", user.id)
    .maybeSingle();

  cachedOrgId = sale?.org_id ?? null;
  return cachedOrgId;
};

export const clearOrgIdCache = () => {
  cachedOrgId = undefined;
};

export const stampOrgId = async (resource: string, data: any) => {
  if (!data) return data;
  if (!TENANT_TABLES.has(resource)) return data;
  if (data.org_id) return data;

  const orgId = await getCurrentOrgId();
  if (!orgId) return data;

  return { ...data, org_id: orgId };
};

// ---------------------------------------------------------------------------
// create overrides
// ---------------------------------------------------------------------------

export const handleCreate = async (
  baseDataProvider: DataProvider,
  resource: string,
  params: any,
) => {
  // 👇 Stamp org_id on every tenant-table create

  console.log("[create.ts] handleCreate called:", resource); // 👈 LINE 1

  params = {
    ...params,
    data: await stampOrgId(resource, params.data),
  };

  if (resource === "contacts") {
    // ... rest is unchanged
    const { data, error } = await supabase
      .from("contacts")
      .insert(params.data)
      .select()
      .single();

    if (error) {
      console.error("[DataProvider] contacts create error:", error);
      throw new Error(`Failed to create contact: ${error.message}`);
    }

    // In create.ts, before the enrichment_queue insert:
    const autoEnrich =
      typeof window !== "undefined" &&
      JSON.parse(
        localStorage.getItem("RaStoreneXus.crm.enrichment.autoOnImport") ?? "null",
      ) === true;

    if (autoEnrich) {
      void supabase.from("enrichment_queue").insert({
        resource: "contacts", // or "contacts"
        record_id: data.id,
        org_id: data.org_id,
        status: "pending",
      })
    .then(({ error }) => {
        if (error) console.error("[enqueue] contacts failed:", error);
        else console.log("[enqueue] contacts queued:", data.id);
      });
    } else {
      console.log("[enqueue] skipped (auto-enrich OFF)", resource, data.id);
    }


    const enriched = await fetchEnrichedRecord("contacts_summary", data.id);
    return enriched ?? { data };
  }

  if (resource === "companies") {
    const { data, error } = await supabase
      .from("companies")
      .insert({
        ...params.data,
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error("[DataProvider] companies create error:", error);
      throw new Error(`Failed to create company: ${error.message}`);
    }

    // 👇 ADD THE TOGGLE CHECK
    const autoEnrich =
      typeof window !== "undefined" &&
      JSON.parse(
        localStorage.getItem("RaStoreneXus.crm.enrichment.autoOnImport") ?? "null",
      ) === true;

    if (autoEnrich) {
      void supabase
        .from("enrichment_queue")
        .insert({
          resource: "companies",
          record_id: data.id,
          org_id: data.org_id,
          status: "pending",
        })
        .then(({ error }) => {
          if (error) console.error("[enqueue] companies failed:", error);
          else console.log("[enqueue] companies queued:", data.id);
        });
    } else {
      console.log("[enqueue] skipped (auto-enrich OFF) companies", data.id);
    }

    const enriched = await fetchEnrichedRecord("companies_summary", data.id);
    return enriched ?? { data };
  }

  if (resource === "tasks") {
    const cleanData = stripTaskSummaryFields(params.data);

    const { data, error } = await supabase
      .from("tasks")
      .insert(cleanData)
      .select()
      .single();

    if (error) {
      console.error("[DataProvider] tasks create error:", error);
      throw new Error(`Failed to create task: ${error.message}`);
    }

    const enriched = await fetchEnrichedRecord("tasks_summary", data.id);
    return enriched ?? { data };
  }

  if (resource === "deals") {
    const cleanData = stripDealSummaryFields(params.data);

    const { data, error } = await supabase
      .from("deals")
      .insert(cleanData)
      .select()
      .single();

    if (error) {
      console.error("[DataProvider] deals create error:", error);
      throw new Error(`Failed to create deal: ${error.message}`);
    }

    const enriched = await fetchEnrichedRecord("deals_summary", data.id);
    return enriched ?? { data };
  }

  if (resource === "invoices") {
    const { items, ...rawData } = params.data;
    const cleanData = stripInvoiceSummaryFields(rawData);

    const { data: createdInvoice, error } = await supabase
      .from("invoices")
      .insert({
        ...cleanData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error("[DataProvider] invoices create error:", error);
      throw new Error(`Failed to create invoice: ${error.message}`);
    }

    if (items && items.length > 0) {
      await Promise.all(
        items.map((item: any) => {
          const { id: _id, ...itemData } = item;
          return baseDataProvider.create("invoice_items", {
            data: { ...itemData, invoice_id: createdInvoice.id },
          });
        }),
      );
    }

    const enriched = await fetchEnrichedRecord(
      "invoices_summary",
      createdInvoice.id,
    );
    return enriched ?? { data: createdInvoice };
  }

  // ✅ Add note handling
  if (
    resource === "contactNotes" ||
    resource === "companyNotes" ||
    resource === "dealNotes"
  ) {
    const { data, error } = await supabase
      .from(resource)
      .insert({
        ...params.data,
        sales_id: params.data.sales_id || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error(`[DataProvider] ${resource} create error:`, error);
      throw new Error(`Failed to create note: ${error.message}`);
    }

    return { data };
  }

  // 👇 new
  if (resource === "crm_activities") {
    const cleanData = stripCrmActivityFields(params.data);

    const { data, error } = await supabase
      .from("crm_activities")
      .insert({
        ...cleanData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error("[DataProvider] crm_activities create error:", error);
      throw new Error(`Failed to create activity: ${error.message}`);
    }

    return { data };
  }

  return baseDataProvider.create(resource, params);
};
